import React, { useCallback, useMemo } from 'react';

import { Session, Slot } from '@yandex-video-platform/goloom-sdk';
import {
    CustomSlot,
    ParticipantsRenderer,
    SelfSharingOptions,
    SelfViewOptions,
} from '@yandex-video-platform/goloom-sdk-react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { useSelector } from 'react-redux';

import { Button, SvgImage } from '../../../../../../shared/components';
import { getShowMyVideo } from '../../../../../../shared/features/settings';
import Invite from '../../../../components/invite';
import ParticipantComponent from '../../../../components/participant';
import { SelfScreenSharingStubLoader } from '../../../../components/participant/self-screen-sharing-stub';
import { GRID_ITEM_RATIO, GRID_VIEW, N_LAST_OPTION } from '../../../../constants';
import { getDisplayMediaStatus, getParticipantsCount } from '../../../../selectors';
import { OutgoingCallStatuses, Participant } from '../../../../types';
import { FullscreenButton } from '../../../fullscreen';
import { useLayoutStyles } from '../../hooks/useLayoutStyles';
import { ParticipantControlsLayer } from '../ParticipantControlsLayer';
import { PlaceholderControlsLayer } from '../PlaceholderControlsLayer';
import { SelfSharingControlsLayer } from '../SelfSharingControlsLayer';
import { SelfViewControlsLayer } from '../SelfViewControlsLayer';
import { getSlotsClassNames } from './helpers/get-slots-class-names';
import css from './styles.css';

const arrowLeftIcon = <SvgImage imageName="arrowLeft" />;
const arrowRightIcon = <SvgImage imageName="arrowRight" />;

const EmptyLayer: React.FC = () => null;

interface Props {
    /**
     * Нужно ли рендерить блок с приглашением
     */
    shouldRenderInviteBlock: boolean;
    /**
     * Соотношение сторон видео
     */
    itemRatio: GRID_ITEM_RATIO;
    /**
     * Вид видео грида (сетка || преентационный)
     */
    gridView: GRID_VIEW;
    /**
     * Инстанс сессии голума
     */
    session: Session;
    /**
     * Текущая страница
     */
    currentPage: number;
    /**
     * Максимальное кол-во отображаемых участников
     */
    maxDisplayParticipants: number;
    /**
     * Нужно ли показывать мое видео
     */
    needShowMyVideo: boolean;
    /**
     * Нужно ли показывать экран информировании о шаринге
     */
    needShowSelfScreenSharing: boolean;
    /**
     * Событие клика на копку предидущей страницы
     */
    onPrevPageClick(): void;
    /**
     * Событие клика на копку следующей страницы
     */
    onNextPageClick(): void;

    /**
     * Отражаем ли видео
     */
    isMirrorVideo: boolean;
    /**
     * Включен ли шаринг экрана у кого-либо
     */
    isScreenSharingActive?: boolean;
    /**
     * Обработка события ресайза
     */
    onResize(elem?: Element): void;

    /**
     * Параметры для отображения окна дозвона в Мессенджере
     */
    isOneOnOne?: boolean;
    callee?: Participant;
    isWaitingForCallee?: boolean;
    callStatus?: OutgoingCallStatuses;

    /**
     * Скрывает видео других участников
     */
    hideParticipantsVideo?: boolean;
    /**
     * Включена ли новая сетка
     */
    isNewGrid?: boolean;
    /**
     * Конструктор ResizeObserver для контейнера сетки.
     * Передавайте `pipWindow.ResizeObserver`, если рендерим в PiP-окно
     * (см. DocumentPictureInPicture). По умолчанию используется
     * глобальный `ResizeObserver` текущего окна.
     */
    ResizeObserverCtor?: typeof ResizeObserver;
    /**
     * Нужно ли показывать кнопку предыдущей страницы
     */
    showPrevPageButton?: boolean;
    /**
     * Нужно ли показывать кнопку следующей страницы
     */
    showNextPageButton?: boolean;
    /**
     * Максимальное количество слотов, для сетки с докладчиком может отличаться от maxDisplayParticipants
     */
    maxSlotsCount: number;
    /**
     * Shell-формат или нет
     */
    isShellEnabled?: boolean;
    /**
     * Нужно ли показывать оконный fullscreen встречи поверх видеогрида
     */
    showMeetingFullscreenButton?: boolean;
    /**
     * Нужно ли фиксировать кнопку оконного fullscreen относительно viewport iframe
     */
    pinMeetingFullscreenButton?: boolean;
}

export const SlotsRenderer: React.FC<Props> = (props) => {
    const {
        itemRatio,
        shouldRenderInviteBlock,
        session,
        currentPage,
        maxDisplayParticipants,
        gridView,
        needShowMyVideo,
        needShowSelfScreenSharing,
        onPrevPageClick,
        onNextPageClick,
        isMirrorVideo,
        isScreenSharingActive,
        onResize,
        isOneOnOne,
        callee,
        isWaitingForCallee,
        callStatus,
        hideParticipantsVideo,
        isNewGrid,
        ResizeObserverCtor,
        showPrevPageButton,
        showNextPageButton,
        maxSlotsCount,
        isShellEnabled = false,
        showMeetingFullscreenButton = false,
        pinMeetingFullscreenButton = false,
    } = props;

    const shouldRenderPendingCallee = Boolean(callee) && isWaitingForCallee && callStatus;

    const displayMediaStatus = useSelector(getDisplayMediaStatus);
    const shouldRenderSyntheticSelfSharingBlock = needShowSelfScreenSharing && !displayMediaStatus;

    // Кастомыне слоты ПОСЛЕ слотов участников/шаринга
    const syntheticEndNodeCount = [shouldRenderInviteBlock, shouldRenderPendingCallee].filter(Boolean).length;
    // Кастомные слоты ДО слотов участников/шаринга
    const syntheticStartNodeCount = [shouldRenderSyntheticSelfSharingBlock].filter(Boolean).length;

    const { getLayout, gridLayoutStyles, resize, startButtonStyles, endButtonStyles, getSlotsSpec } = useLayoutStyles(
        gridView,
        {
            nodeItemRatio: itemRatio,
            syntheticEndNodeCount,
            syntheticStartNodeCount,
            maxDisplayNodeCount: maxDisplayParticipants,
            page: currentPage,
            handleResize: onResize,
            shouldSwapSelfView: Boolean(shouldRenderPendingCallee),
            isScreenSharingActive,
            isNewGrid,
            ResizeObserverCtor,
        }
    );

    const isPresenterView = gridView === GRID_VIEW.PRESENTER;

    const startCustomSlots = useMemo<CustomSlot[]>(() => {
        const slots: CustomSlot[] = [];

        if (showPrevPageButton && !isShellEnabled) {
            slots.push({
                component: () => (
                    <Button
                        className={css.pageButton}
                        view="neutral"
                        pin="round-round"
                        size="md"
                        icon={arrowLeftIcon}
                        onClick={onPrevPageClick}
                    />
                ),
                inlineStyle: startButtonStyles,
                className: css.itemStart,
            });
        }

        if (shouldRenderPendingCallee && callee) {
            slots.push({
                component: () => (
                    <ParticipantComponent
                        id={callee.id}
                        shouldRenderAvatar={callee.shouldRenderAvatar}
                        avatarUrl={callee.avatarUrl}
                        style={{ width: '100%', height: '100%' }}
                        displayName={callee.displayName}
                        isOneOnOne={isOneOnOne}
                        isPending={isWaitingForCallee}
                        isCallee={true}
                        callStatus={callStatus}
                    />
                ),
                inlineStyle: gridLayoutStyles[1],
            });
        }

        if (shouldRenderSyntheticSelfSharingBlock) {
            slots.push({
                component: () => <SelfScreenSharingStubLoader />,
                inlineStyle: gridLayoutStyles[0],
            });
        }

        return slots;
    }, [
        showPrevPageButton,
        isShellEnabled,
        shouldRenderPendingCallee,
        callee,
        shouldRenderSyntheticSelfSharingBlock,
        startButtonStyles,
        onPrevPageClick,
        gridLayoutStyles,
        isOneOnOne,
        isWaitingForCallee,
        callStatus,
    ]);

    const endCustomSlots = useMemo<CustomSlot[]>(() => {
        const slots: CustomSlot[] = [];
        const className = cn(css.itemEnd);

        if (shouldRenderInviteBlock) {
            slots.push({
                component: Invite,
                inlineStyle: gridLayoutStyles[gridLayoutStyles.length - 1],
                className: className,
            });
        }

        if (showNextPageButton && !isShellEnabled) {
            slots.push({
                component: () => (
                    <Button
                        className={css.pageButton}
                        view="neutral"
                        pin="round-round"
                        size="md"
                        icon={arrowRightIcon}
                        onClick={onNextPageClick}
                    />
                ),
                inlineStyle: endButtonStyles,
                className: className,
            });
        }

        return slots;
    }, [
        shouldRenderInviteBlock,
        isShellEnabled,
        showNextPageButton,
        onNextPageClick,
        gridLayoutStyles,
        endButtonStyles,
    ]);

    const selfViewOptions = useMemo<SelfViewOptions>(() => {
        return {
            isEnabled: needShowMyVideo,
            mirrorVideo: isMirrorVideo,
        };
    }, [needShowMyVideo, isMirrorVideo]);

    const selfSharingOptions = useMemo<SelfSharingOptions>(() => {
        return {
            isEnabled: needShowSelfScreenSharing,
        };
    }, [needShowSelfScreenSharing]);

    const lastNOptions = useMemo(() => {
        if (isPresenterView && !isScreenSharingActive) {
            return N_LAST_OPTION;
        }
    }, [isPresenterView, isScreenSharingActive]);

    const getSlotsClassNamesFunc = useCallback(
        (slots: Slot[]) => getSlotsClassNames({ slots, videoStatus: session.videoStatus }),
        [session.videoStatus]
    );

    const showMyVideo = useSelector(getShowMyVideo);
    const participantsCount = useSelector(getParticipantsCount);
    const slotsSpec = useMemo(() => {
        const currentSlotsCount =
            (participantsCount + (isPresenterView ? 1 : 0) - (!showMyVideo ? 1 : 0)) % (maxSlotsCount + 1);
        const totalSlotsCount = currentSlotsCount + syntheticEndNodeCount + syntheticStartNodeCount;
        let newSlotsSpec = getSlotsSpec(totalSlotsCount);
        while (newSlotsSpec.length < maxSlotsCount) {
            newSlotsSpec = [
                ...newSlotsSpec,
                getSlotsSpec(newSlotsSpec.length + 1).pop() as { width: number; height: number },
            ];
        }
        return newSlotsSpec;
    }, [
        getSlotsSpec,
        isPresenterView,
        maxSlotsCount,
        participantsCount,
        showMyVideo,
        syntheticEndNodeCount,
        syntheticStartNodeCount,
    ]);

    return (
        <div className={css.root}>
            <div
                data-video-root
                className={cn(css.container, {
                    [css.presenter]: isPresenterView,
                    [css.newGridContainer]: isNewGrid,
                })}
                ref={resize.refCallback}
            >
                <ParticipantsRenderer
                    getLayout={getLayout}
                    session={session}
                    selfViewOptions={selfViewOptions}
                    selfSharingOptions={selfSharingOptions}
                    maxSlotsCount={maxSlotsCount}
                    onVisibleItemsChange={noop}
                    ParticipantControlsLayerRenderer={isShellEnabled ? EmptyLayer : ParticipantControlsLayer}
                    PlaceholderControlsLayerRenderer={isShellEnabled ? EmptyLayer : PlaceholderControlsLayer}
                    SelfViewControlsLayerRenderer={isShellEnabled ? EmptyLayer : SelfViewControlsLayer}
                    SelfSharingControlsLayerRenderer={isShellEnabled ? EmptyLayer : SelfSharingControlsLayer}
                    startCustomSlots={startCustomSlots}
                    endCustomSlots={endCustomSlots}
                    pageNumber={currentPage + 1}
                    classNames={{
                        slot: cn(css.item, { [css.newGridItem]: isNewGrid }),
                    }}
                    getSlotsClassNames={getSlotsClassNamesFunc}
                    nLastOptions={lastNOptions}
                    shutdownAllVideo={hideParticipantsVideo}
                    slotsSpec={slotsSpec}
                />
                {showMeetingFullscreenButton && <FullscreenButton isPinned={pinMeetingFullscreenButton} />}
            </div>
        </div>
    );
};
